import { Box, Typography, Avatar } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VideocamIcon from "@mui/icons-material/Videocam";
import CallIcon from "@mui/icons-material/Call";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import WifiIcon from "@mui/icons-material/Wifi";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import SentimentSatisfiedAltOutlinedIcon from "@mui/icons-material/SentimentSatisfiedAltOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";

// Turns WhatsApp's own inline markdown (*bold*, _italic_, ~strikethrough~) into styled
// spans, so the preview shows text the way it will actually render on a phone, not the
// raw asterisks/underscores someone typed.
function renderWhatsAppMarkdown(text) {
  if (!text) return null;
  const parts = text.split(/(\*[^*]+\*|_[^_]+_|~[^~]+~)/g).filter(Boolean);
  return parts.map((part, i) => {
    if (part.startsWith("*") && part.endsWith("*")) return <strong key={i}>{part.slice(1, -1)}</strong>;
    if (part.startsWith("_") && part.endsWith("_")) return <em key={i}>{part.slice(1, -1)}</em>;
    if (part.startsWith("~") && part.endsWith("~")) return <span key={i} style={{ textDecoration: "line-through" }}>{part.slice(1, -1)}</span>;
    return <span key={i}>{part}</span>;
  });
}

const BUTTON_ICON = {
  QUICK_REPLY: ReplyRoundedIcon,
  URL: OpenInNewRoundedIcon,
  PHONE_NUMBER: CallRoundedIcon,
};

// Fills {{1}}, {{2}}... in text with the given sample values, falling back to the
// placeholder itself when a sample hasn't been typed yet — mirrors bodyPreview() in
// utils/whatsappTemplate.js but kept local since it also needs to feed markdown rendering.
function fillPlaceholders(text, samples) {
  if (!text) return "";
  return text.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n) => samples?.[Number(n) - 1] || `{{${n}}}`);
}

// WhatsApp's real default wallpaper (light theme) — a warm beige base with a faint
// repeating doodle texture. Approximated with layered diagonal hatching rather than flat
// dots, since a solid color or plain dot-grid reads as an obvious placeholder, not a
// real chat screen.
const WALLPAPER = {
  backgroundColor: "#E9E0D2",
  backgroundImage: [
    "repeating-linear-gradient(45deg, rgba(0,0,0,0.028) 0px, rgba(0,0,0,0.028) 1px, transparent 1px, transparent 14px)",
    "repeating-linear-gradient(-45deg, rgba(0,0,0,0.022) 0px, rgba(0,0,0,0.022) 1px, transparent 1px, transparent 14px)",
  ].join(", "),
};

const emptyMediaSx = {
  bgcolor: "#E9EDEF",
  borderRadius: "8px",
  height: 140,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1.5px dashed #C7CDD1",
};

function HeaderMedia({ headerType, mediaUrl, fileName }) {
  if (headerType === "IMAGE") {
    return mediaUrl ? (
      <Box
        component="img"
        src={mediaUrl}
        alt=""
        sx={{ width: "100%", height: 140, objectFit: "cover", borderRadius: "8px", mb: 0.75, display: "block" }}
      />
    ) : (
      <Box sx={{ ...emptyMediaSx, mb: 0.75 }}>
        <ImageOutlinedIcon sx={{ fontSize: 30, color: "#8696A0" }} />
      </Box>
    );
  }
  if (headerType === "VIDEO") {
    return mediaUrl ? (
      <Box sx={{ position: "relative", mb: 0.75 }}>
        <Box component="video" src={mediaUrl} sx={{ width: "100%", height: 140, objectFit: "cover", borderRadius: "8px", display: "block" }} />
        <Box
          sx={{
            position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "rgba(0,0,0,0.25)", borderRadius: "8px",
          }}
        >
          <VideocamOutlinedIcon sx={{ fontSize: 34, color: "#fff" }} />
        </Box>
      </Box>
    ) : (
      <Box sx={{ ...emptyMediaSx, mb: 0.75 }}>
        <VideocamOutlinedIcon sx={{ fontSize: 30, color: "#8696A0" }} />
      </Box>
    );
  }
  if (headerType === "DOCUMENT") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "#F0F2F5", borderRadius: "8px", p: 1, mb: 0.75 }}>
        <DescriptionOutlinedIcon sx={{ fontSize: 26, color: "#EA4335" }} />
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#3B4A54", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {fileName || "document.pdf"}
        </Typography>
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: "#8696A0", ml: "auto" }} />
      </Box>
    );
  }
  return null;
}

function WhatsAppTemplatePreview({
  businessName = "Idiotic Media",
  headerType = "NONE",
  headerText = "",
  headerMediaUrl = null,
  headerFileName = "",
  bodyText = "",
  bodyVariableSamples = [],
  footerText = "",
  buttons = [],
}) {
  const filledBody = fillPlaceholders(bodyText, bodyVariableSamples);
  const now = new Date();
  const time12 = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" });
  const bubbleTime = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
  const hasMediaHeader = ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerType);

  return (
    <Box
      sx={{
        width: 300,
        borderRadius: "36px",
        border: "10px solid #0B0B0C",
        bgcolor: "#000",
        overflow: "hidden",
        boxShadow: "0 16px 40px rgba(0,0,0,0.32), 0 2px 6px rgba(0,0,0,0.15)",
        mx: "auto",
        position: "relative",
      }}
    >
      {/* Notch */}
      <Box sx={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: 90, height: 18, bgcolor: "#000", borderRadius: "0 0 12px 12px", zIndex: 3 }} />

      {/* Status bar */}
      <Box sx={{ height: 30, bgcolor: "#054C40", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.25, pt: 0.5 }}>
        <Typography sx={{ color: "#fff", fontSize: 11.5, fontWeight: 700 }}>{time12}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <SignalCellularAltIcon sx={{ fontSize: 13, color: "#fff" }} />
          <WifiIcon sx={{ fontSize: 13, color: "#fff" }} />
          <BatteryFullIcon sx={{ fontSize: 15, color: "#fff" }} />
        </Box>
      </Box>

      {/* Chat header */}
      <Box sx={{ bgcolor: "#075E54", color: "#fff", px: 1.25, py: 0.9, display: "flex", alignItems: "center", gap: 1 }}>
        <ArrowBackIcon fontSize="small" />
        <Avatar sx={{ width: 30, height: 30, bgcolor: "#25D366", fontSize: 14, fontWeight: 700 }}>
          {businessName?.[0]?.toUpperCase() || "B"}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13.5, fontFamily: "Quicksand", lineHeight: 1.2 }} noWrap>
            {businessName}
          </Typography>
          <Typography sx={{ fontSize: 10.5, opacity: 0.75, lineHeight: 1.2 }}>online</Typography>
        </Box>
        <Box sx={{ ml: "auto", display: "flex", gap: 1.25, opacity: 0.9 }}>
          <VideocamIcon sx={{ fontSize: 18 }} />
          <CallIcon sx={{ fontSize: 17 }} />
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </Box>
      </Box>

      {/* Chat scroll area — minHeight (not a fixed height) + overflowY:auto so a body
          longer than a short template still fully renders and scrolls instead of being
          clipped. Bottom-anchoring the bubble via `mt: "auto"` on it (not
          justify-content:flex-end on this container) matters here: flexbox's
          justify-content:flex-end has a well-known bug where, once content overflows, the
          start of that content becomes unreachable even while scrolled all the way up —
          which is exactly the "top of the message is cut off" bug this replaces. */}
      <Box
        sx={{
          minHeight: 400,
          maxHeight: 560,
          p: 1.5,
          ...WALLPAPER,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        <Box sx={{ maxWidth: "88%", minWidth: 0, mt: "auto" }}>
          <Box
            sx={{
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              bgcolor: "#fff",
              borderRadius: buttons.length ? "8px 8px 0 0" : "8px 8px 8px 2px",
              p: 1.25,
              boxShadow: "0 1px 2px rgba(0,0,0,0.18)",
            }}
          >
            {headerType === "TEXT" && headerText && (
              <Typography sx={{ fontWeight: 700, fontSize: 13.5, mb: 0.5, fontFamily: "Quicksand" }}>
                {renderWhatsAppMarkdown(headerText)}
              </Typography>
            )}
            {hasMediaHeader && <HeaderMedia headerType={headerType} mediaUrl={headerMediaUrl} fileName={headerFileName} />}

            <Typography sx={{ fontSize: 13.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word", lineHeight: 1.5, fontFamily: "Quicksand", color: "#1C1C2E" }}>
              {filledBody ? renderWhatsAppMarkdown(filledBody) : (
                <span style={{ color: "#B0B7BF", fontStyle: "italic" }}>Body text appears here…</span>
              )}
            </Typography>
            {footerText && (
              <Typography sx={{ fontSize: 11.5, color: "#8696A0", mt: 0.5 }}>{footerText}</Typography>
            )}
            <Typography sx={{ fontSize: 10, color: "#8696A0", textAlign: "right", mt: 0.5, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.3 }}>
              {bubbleTime} <DoneAllRoundedIcon sx={{ fontSize: 13, color: "#53BDEB" }} />
            </Typography>
          </Box>

          {buttons.length > 0 && (
            <Box sx={{ bgcolor: "#fff", borderRadius: "0 0 8px 8px", boxShadow: "0 1px 2px rgba(0,0,0,0.18)" }}>
              {buttons.map((b, i) => {
                const Icon = BUTTON_ICON[b.type] || ReplyRoundedIcon;
                return (
                  <Box
                    key={i}
                    sx={{
                      borderTop: "1px solid #EDEFF1",
                      py: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.6,
                      color: "#00A5F4",
                      fontWeight: 600,
                      fontSize: 13,
                      fontFamily: "Quicksand",
                    }}
                  >
                    <Icon sx={{ fontSize: 15 }} />
                    {b.text || "Button"}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* Message compose bar */}
      <Box sx={{ bgcolor: "#F0F2F5", px: 1, py: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box
          sx={{
            flex: 1,
            bgcolor: "#fff",
            borderRadius: "20px",
            px: 1.25,
            py: 0.6,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            minWidth: 0,
          }}
        >
          <SentimentSatisfiedAltOutlinedIcon sx={{ fontSize: 19, color: "#8696A0" }} />
          <Typography sx={{ fontSize: 12.5, color: "#8696A0", flex: 1 }} noWrap>
            Message
          </Typography>
          <AttachFileIcon sx={{ fontSize: 17, color: "#8696A0", transform: "rotate(45deg)" }} />
          <CameraAltOutlinedIcon sx={{ fontSize: 18, color: "#8696A0" }} />
        </Box>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            bgcolor: "#25D366",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <MicNoneOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
        </Box>
      </Box>

      {/* Home indicator */}
      <Box sx={{ bgcolor: "#F0F2F5", display: "flex", justifyContent: "center", py: 0.6 }}>
        <Box sx={{ width: 110, height: 4, borderRadius: 2, bgcolor: "#0006" }} />
      </Box>
    </Box>
  );
}

export default WhatsAppTemplatePreview;
